import { useState, useRef, useEffect } from "react";
import { Camera, Loader2 } from "lucide-react";
import { ImageCropper } from "@/components/image-cropper";

export function PhotoUploadSlot({
  label,
  currentUrl,
  uploading,
  onUpload,
  testId,
  aspect = 16 / 9,
}: {
  label: string;
  currentUrl: string | null;
  uploading: boolean;
  onUpload: (file: File) => void;
  testId: string;
  aspect?: number;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const handleCropperOpenChange = (open: boolean) => {
    setShowCropper(open);
    if (!open) setCropSrc(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setCropSrc(reader.result as string);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleCropComplete = (blob: Blob) => {
    if (preview) URL.revokeObjectURL(preview);
    const croppedUrl = URL.createObjectURL(blob);
    setPreview(croppedUrl);
    const croppedFile = new File([blob], "cropped.jpg", { type: "image/jpeg" });
    onUpload(croppedFile);
  };

  const displayUrl = preview || currentUrl;
  const imgHeight = aspect === 1 ? "h-32 w-32" : "h-28";

  return (
    <div className="rounded-lg border bg-secondary/30 p-3 text-center space-y-2">
      <p className="text-xs text-muted-foreground font-medium">{label}</p>
      {displayUrl ? (
        <div className="relative group">
          <img src={displayUrl} alt={label} className={`w-full ${imgHeight} object-cover rounded-md`} />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <Camera className="w-5 h-5 text-white" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className={`w-full ${imgHeight} rounded-md border-2 border-dashed border-muted-foreground/30 flex flex-col items-center justify-center gap-1 hover:bg-secondary/50 transition-colors cursor-pointer`}
        >
          {uploading ? (
            <Loader2 className="w-6 h-6 text-muted-foreground/50 animate-spin" />
          ) : (
            <>
              <Camera className="w-6 h-6 text-muted-foreground/50" />
              <span className="text-[10px] text-muted-foreground">Choisir / Prendre photo</span>
            </>
          )}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleChange}
        data-testid={testId}
      />
      {uploading && (
        <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
          <Loader2 className="w-3 h-3 animate-spin" />Upload...
        </div>
      )}
      {showCropper && cropSrc && (
        <ImageCropper
          open={showCropper}
          onOpenChange={handleCropperOpenChange}
          imageSrc={cropSrc}
          aspect={aspect}
          onCropComplete={handleCropComplete}
        />
      )}
    </div>
  );
}
